import React from "react";

import { EventPerformance, timeData } from '../../models/models';

import ValidationService, { Schema, COMMON_REGEXES } from "../../services/validation.service";
import EventService from "../../services/event.service";
import { StorageContext } from "../storage/storage-context";
import config from "../../config/config";

// import QuickForm, { QuickFormSchemaMetaType } from "../quick-form/quick-form";
import QuickForm, { QuickFormSchemaMetaType, quickFormPartialInputHandler } from "../utilities/quick-form/quick-form";
import FileBrowser from "../file-browser/file-browser";
import { ModalContext } from "../modal/modal";
import { SCRUDFormComponent } from "../utilities/SCRUD/SCRUD";

import TwoColumns from "../utilities/two-columns/two-columns";
import ColumnSnapper from "../utilities/column-snapper/column-snapper";

import AutocompleteInput from "../utilities/quick-form/custom-inputs/autocomplete-input";
import FileBrowserInput from "../utilities/quick-form/custom-inputs/file-browser-input";

import Icons from "../icons/icons";

const { periods, weekdays, months, daysPerMonth, years, dates, times } = timeData;

const periodPrases = (e: EventPerformance | FormEvent): string => {
  if (
    ([...dates] as any[]).includes(e.day) &&
    ([...months] as any[]).includes(e.month) &&
    ([...years] as any[]).includes(e.year) &&
    ([...periods] as any[]).includes(e.period)
  ) {
    let { year, month, day, period } = (e as EventPerformance);
    const dayNum = (new Date(year, months.indexOf(month), day)).getDay();
    const weekday: typeof weekdays[number] = weekdays[dayNum];
    const dayCount = Math.floor((day-1) / 7) + 1;
    const dayCountSuffix = [ 'st', 'nd', 'rd', 'th', 'th' ];
    return ({
      Once: ``,
      Daily: `Every day`,
      Weekly: `Every ${weekday}`,
      BiWeekly: `Every other ${weekday}`,
      Monthly: `The ${dayCount}${dayCountSuffix[dayCount-1]} ${weekday} of every month`
    })[period];
  }
  return '';
};

type FormEvent = Omit<EventPerformance, "month" | "day" | "year" | "time" | "period"> & { 
  day: "Day" | EventPerformance["day"],
  month: "Month" | EventPerformance["month"],
  year: "Year" | EventPerformance["year"],
  time: "Time" | EventPerformance["time"],
  period: "Period" | EventPerformance["period"]
}

const EventSCRUDForm: React.FC<SCRUDFormComponent<EventPerformance>> = ({ initModel, onSubmit, onCancel }) => {

  const modalContext = React.useContext(ModalContext);
  const [storageContext, setStorageContext] = React.useContext(StorageContext);

  const [model, setModel] = React.useState<FormEvent>(initModel ? {
    id: initModel.id, timestamp: initModel.timestamp,
    day: initModel.day, month: initModel.month, year: initModel.year,
    time: initModel.time, website: initModel.website || '', location: initModel.location || '',
    addressa: initModel.addressa || "", addressb: initModel.addressb || "",
    thumbnail: initModel.thumbnail || '', description: initModel.description || '', period: initModel.period, media: initModel.media || []
  } : {
    id: -1, timestamp: -1,
    day: "Day", month: 'Month', year: "Year",
    time: "Time", website: '', location: '',
    addressa: "", addressb: "",
    thumbnail: '', description: '', period: 'Period', media: []
  });
  const [formErrors, setFormErrors] = React.useState<{ key: string, message: string }[]>([]);
  const [touched, setTouched] = React.useState<boolean>(false);

  const eventSchema: Schema<QuickFormSchemaMetaType> = React.useMemo(() => ({
    day: { type: [...dates.map(d => d.toString())], attributes: { required: true, range: { min: 1, max: 31 } }, meta: { quickForm: { hideErrorMessages: true }} },
    month: { type: [...months], attributes: { required: true }, meta: { quickForm: { hideErrorMessages: true }}  },
    year: { type: 'number', attributes: { required: true, range: { min: 2020, max: 2120 } }, meta: { quickForm: { hideErrorMessages: true }}  },
    time: { type: [...times], attributes: { required: true }, meta: { quickForm: { hideErrorMessages: true }} },
    location: { type: 'string', attributes: { required: true }, 
      meta: { 
        quickForm: { 
          hideErrorMessages: true,
          CustomInput: AutocompleteInput,
          customInputProps: {
            suggestions: [
              ...(storageContext?.events as EventPerformance[])?.map(e => e.location),
              // ...[
              //   'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio',
              //   'San Diego', 'Dallas', 'San Jose', 'Austin', 'Jacksonville', 'Fort Worth', 'Columbus',
              //   'Charlotte', 'San Francisco', 'Indianapolis', 'Seattle', 'Denver', 'Washington D.C.'
              // ]
            ]
            // badge: Icons.check
          }
        }
      } 
    },
    addressa: { type: 'string', attributes: { required: false }, 
      meta: { 
        quickForm: { 
          hideErrorMessages: true,
          CustomInput: AutocompleteInput,
          placeholder: 'Address Line 1',
          customInputProps: {
            suggestions: [
              ...(storageContext?.events as EventPerformance[])?.map(e => e.addressa),
              // ...[
              //   '123 Main St', '456 Elm St', '789 Oak St', '124 S Poplar St',
              //   'City Hall', 'Community Center', 'Central Park',
              //   'Convention Center', 'Downtown Plaza', 'Riverside Venue',
              //   'Grand Theater', 'Liberty Hall', 'Sunset Pavilion'
              // ]
            ]
            // badge: Icons.check
          }
        }
      } 
    },
    addressb: { type: 'string', attributes: { required: false }, 
      meta: { 
        quickForm: { 
          hideErrorMessages: true,
          CustomInput: AutocompleteInput,
          placeholder: 'Address Line 2',
          customInputProps: {
            suggestions: [
              ...(storageContext?.events as EventPerformance[])?.map(e => e.addressb),
              // ...[
              //   '123 Main St', '456 Elm St', '789 Oak St',
              //   'City Hall', 'Community Center', 'Central Park',
              //   'Convention Center', 'Downtown Plaza', 'Riverside Venue',
              //   'Grand Theater', 'Liberty Hall', 'Sunset Pavilion'
              // ]
            ]
          }
        }
      } 
    },
    thumbnail: { type: 'string', attributes: { required: false },
      meta: {
        quickForm: {
          hideErrorMessages: true,
          CustomInput: FileBrowserInput,
          customInputProps: {
            acceptedMediaExtensions: { image: ['.jpg', '.jpeg', '.png', '.gif', '.webp'], }
          }
        }
      }
    },
    description: { type: 'string', attributes: { required: true, strLength: { minLength: 5 } }, 
      meta: { 
        quickForm: { 
          hideErrorMessages: true,
          textArea: true, 
          textAreaRows: 4,
          placeholder: 'Write your description here...'
        }
      } 
    },
    website: { type: 'string', attributes: { required: false }, 
      meta: { 
        quickForm: { 
          hideErrorMessages: true,
          CustomInput: AutocompleteInput,
          customInputProps: {
            suggestions: [
              ...(storageContext?.events as EventPerformance[])?.map(e => e.website).filter(w => w),
              // ...[
              //   'https://www.google.com', 'https://www.facebook.com', 'https://www.twitter.com',
              //   'https://www.instagram.com', 'https://www.linkedin.com', 'https://www.youtube.com',
              //   'https://www.eventbrite.com', 'https://www.meetup.com', 'https://www.ticketmaster.com'
              // ]
            ],
          }
        }
      } 
    },
    media: { type: 'string', attributes: { required: false, array: { minLength: 0 }}, meta: { quickForm: { hideErrorMessages: true }} },
    period: { type: [...periods], attributes: { required: true }, meta: { quickForm: { hideErrorMessages: true }}  }
  }), [initModel, storageContext]);

  // React.useEffect(() => {
  //   console.log('model update', model);
  // }, [model]);

  return (
    <div className="inline-block w-full mx-auto pb-8">

      <div className="border border-gray-300 rounded shadow-md p-6">
        <TwoColumns>

          <div className="p-4 flex flex-col items-center ">
            <div 
              className='w-full max-w-xs aspect-1 border border-gray-300 rounded shadow-md p-0 relative cursor-pointer'
            >
              <QuickForm<Pick<FormEvent, "thumbnail">>
                labelPlacement="none"
                schema={{ thumbnail: eventSchema.thumbnail }}
                init={{ thumbnail: model.thumbnail }}
                onInput={(err, m) => {
                  // console.log('thumbnail change', m);
                  quickFormPartialInputHandler<FormEvent>(model, m, err, setModel, setFormErrors, setTouched)
                }}
              />
            </div>

            <button 
              className="btn btn-sm btn-secondary mt-4 !h-9 w-full max-w-xs"
              onClick={async () => {
                const modalRes = await (new Promise<{ filenames: string[] } | null>((res, rej) => modalContext.modal!({
                  node: <MediaBrowserModal resolve={res} initMedia={model.media} />,
                  resolve: res , reject: rej
                })));
                modalContext.modal!();
                if (modalRes && modalRes.filenames) {
                  setModel(prev => ({ ...prev, media: modalRes.filenames.map(f => f.replace(config.ASSETS[config.ENVIRONMENT], '')) }));
                }
              }}
            >
              Additional Media
            </button>

          </div>
          <div className="p-2">
            <div className="text-lg font-semibold flex flex-wrap justify-between items-center w-[calc(100%-16px)] m-2 pb-1">
              <div className="grow shrink-0 min-w-[120px]">
                <QuickForm<Pick<FormEvent, "month">>
                  labelPlacement="none"
                  schema={{ month: eventSchema.month }}
                  init={{ month: model.month }}
                  onInput={(err, m) => {
                    quickFormPartialInputHandler<FormEvent>(model, m, err, setModel, setFormErrors, setTouched)
                  }}
                />
              </div>
              <div className="grow shrink-0 min-w-[96px]">
                <QuickForm<Pick<FormEvent, "day">>
                  labelPlacement="none"
                  schema={{ day: eventSchema.day }}
                  init={{ day: model.day }}
                  onInput={(err, m) => {
                    quickFormPartialInputHandler<FormEvent>(model, m, err, setModel, setFormErrors, setTouched)
                  }}
                />
              </div>
              <div className="hidden md:block">
                &nbsp;,&nbsp;
              </div>
              <div className="grow shrink-0 min-w-[96px]">
                <QuickForm<Pick<FormEvent, "year">>
                  labelPlacement="none"
                  schema={{ year: eventSchema.year }}
                  init={{ year: model.year }}
                  onInput={(err, m) => {
                    quickFormPartialInputHandler<FormEvent>(model, m, err, setModel, setFormErrors, setTouched)
                  }}
                />
              </div>
              <div className="grow shrink-0 min-w-[120px]">
                <QuickForm<Pick<FormEvent, "time">>
                  labelPlacement="none"
                  schema={{ time: eventSchema.time }}
                  init={{ time: model.time }}
                  onInput={(err, m) => {
                    quickFormPartialInputHandler<FormEvent>(model, m, err, setModel, setFormErrors, setTouched)
                  }}
                />
              </div>
              <div className="grow shrink-0 min-w-[120px]">
                <QuickForm<Pick<FormEvent, "period">>
                  labelPlacement="none"
                  schema={{ period: eventSchema.period }}
                  init={{ period: model.period }}
                  onInput={(err, m) => {
                    quickFormPartialInputHandler<FormEvent>(model, m, err, setModel, setFormErrors, setTouched)
                  }}
                />
              </div>
            </div>
            
            <div className="ml-2">
              <TwoColumns leftMinPx={256} rightMinPx={1028}>
                <QuickForm<Pick<FormEvent, "location">>
                  labelPlacement="none"
                  schema={{ location: eventSchema.location }}
                  init={{ location: model.location }}
                  onInput={(err, m) => {
                    quickFormPartialInputHandler<FormEvent>(model, m, err, setModel, setFormErrors, setTouched)
                  }}
                />
                <div className='w-[calc(100%-16px)] flex items-center justify-center pt-1 bg-slate-200 rounded-lg h-12 mx-2'> { periodPrases(model) } </div>
              </TwoColumns>
            </div>

            <div className={`w-[calc(100%-16px)] text-lg font-medium m-2`}>
              <QuickForm<Pick<FormEvent, "addressa" | "addressb" | "description" | "website">>
                labelPlacement="none"
                schema={{
                  addressa: eventSchema.addressa,
                  addressb: eventSchema.addressb,
                  description: eventSchema.description,
                  website: eventSchema.website
                }}
                init={{
                  addressa: model.addressa,
                  addressb: model.addressb,
                  description: model.description,
                  website: model.website
                }}
                onInput={(err, m) => {
                  quickFormPartialInputHandler<FormEvent>(model, m, err, setModel, setFormErrors, setTouched)
                }}
              />
            </div>
          </div>


        </TwoColumns>

      </div>
      <div className="flex mt-3">
        {
          (touched && !!Object.keys(formErrors).length) &&
            // <div className="inline-block alert alert-error mr-2 h-12 pl-10 pt-3">{Object.keys(formErrors)[0] + ": " + formErrors[Object.keys(formErrors)[0]][0]}</div>
            <select 
              className="inline-block alert alert-error mr-2 h-12 pt-4 text-sm">
              { // todo: focus input element corresponding to selected error
                // Object.keys(formErrors).reduce((opts, key) => [...formErrors[key as keyof typeof formErrors].map(err => (<option key={key + " " + err} className="text-xs">{key + " " + err}</option>)), ...opts],([] as React.ReactNode[]))
                formErrors.map((fe, i) => (<option key={i + fe.key + " " + fe.message} className="text-xs">{fe.message}</option>))
              }
            </select>
        }
        <button className={`btn btn-primary mr-2 ml-auto`} disabled={!touched || !!Object.keys(formErrors).length} onClick={() => { 
          onSubmit(model as EventPerformance); 
        }}>
            Submit
        </button>
        <button className="btn btn-warning" onClick={() => { onCancel(); }}>Cancel</button>
      </div>
    </div>
  );
}



const FileBrowserModal: React.FC<{ resolve: (value: { filename: string } | null) => any }> = ({ resolve }) => {

  return (
    <FileBrowser
      acceptedMediaExtensions={{
        image: ['gif', 'jpg', 'jpeg', 'png']
      }}
      onPick={(file) => {
        if (file) {
          resolve({ filename: file });
        } else {
          // resolve(null);
        }
      }}
    />
  );

}

const MediaBrowserModal: React.FC<{ resolve: (value: { filenames: string[] } | null) => any, initMedia: string[] }> = ({ resolve, initMedia }) => {

  const [mediaList, setMediaList] = React.useState<string[]>(initMedia);
  
  return (
    <div>
      <FileBrowser
        acceptedMediaExtensions={{
          image: ['gif', 'jpg', 'jpeg', 'png'],
          video: ['mov', 'mp4', 'mpeg', 'webm', 'ogg'],
          audio: ['mp3', 'wav', 'ogg']
        }}
        onPick={(file) => {
          if (file) {
            // if medialist already contains file, remove it, otherwise add it
            setMediaList(prev => {
              if (prev.includes(file)) {
                return prev.filter(f => f !== file);
              } else {
                return [...prev, file];
              }
            });
          }
        }}
      />
      <div className="flex justify-end mt-4 mx-8 mb-2">
        <select 
          className="select inline-block border border-gray-300 rounded-lg mx-1 w-2/4 flex-1"
          value={mediaList.length ? mediaList.join(', ') : 'No Media Selected'}
        >
          {
            mediaList.length ? 
              mediaList.map((m, i) => <option key={i+m}>{m}</option>) :
              <option>No Media Selected</option>
          }
        </select>
        <button 
          className="btn btn-primary mr-2"
          onClick={() => resolve({ filenames: mediaList })}
        >
          Submit
        </button>
        <button 
          className="btn btn-warning"
          onClick={() => resolve(null)}
        >
          Cancel
        </button>
      </div>
    </div>
  );

}


export default EventSCRUDForm;